import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import { downloadCsv } from "../lib/csv";
import { useOrganization } from "../organization/OrganizationProvider";
import "./ReportsPage.css";

type FinancialSummary = {
  total_assets: number | string;
  total_liabilities: number | string;
  base_equity: number | string;
  total_income: number | string;
  total_expenses: number | string;
  period_result: number | string;
  equity_with_result: number | string;
  liabilities_and_equity: number | string;
  accounting_difference: number | string;
  equation_balanced: boolean;
};

type TrialRow = {
  account_id: string;
  account_code: string;
  account_name: string;
  account_type: string;
  opening_debit: number | string;
  opening_credit: number | string;
  period_debit: number | string;
  period_credit: number | string;
  closing_debit: number | string;
  closing_credit: number | string;
};

type IncomeRow = {
  account_id: string;
  account_code: string;
  account_name: string;
  account_type: string;
  debit_total: number | string;
  credit_total: number | string;
  report_balance: number | string;
};

type PositionRow = IncomeRow & {
  signed_balance: number | string;
};

type LedgerRow = {
  entry_id: string;
  entry_number: string;
  entry_date: string;
  description: string;
  source_type: string | null;
  source_id: string | null;
  line_description: string | null;
  debit: number | string;
  credit: number | string;
  running_balance: number | string;
};

type AccountRow = {
  id: string;
  code: string;
  name: string;
  account_type: string;
  accepts_entries: boolean;
  status: string;
};

type ReportTab = "summary" | "trial" | "income" | "position" | "ledger";

function money(value: number | string | undefined) {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
  }).format(Number(value || 0));
}

function today() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Lima",
  }).format(new Date());
}

function firstDayOfMonth() {
  const parts = today().split("-");
  return parts[0] + "-" + parts[1] + "-01";
}

export function ReportsPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [tab, setTab] = useState<ReportTab>("summary");
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth());
  const [dateTo, setDateTo] = useState(today());
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [accountId, setAccountId] = useState("");

  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [trial, setTrial] = useState<TrialRow[]>([]);
  const [income, setIncome] = useState<IncomeRow[]>([]);
  const [position, setPosition] = useState<PositionRow[]>([]);
  const [ledger, setLedger] = useState<LedgerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAccounts = useCallback(async () => {
    if (!organizationId) return;

    const { data, error: rpcError } = await supabase.rpc(
      "list_accounting_accounts_ui",
      { p_organization_id: organizationId },
    );

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    const next = (data ?? []) as AccountRow[];
    setAccounts(next);

    if (!accountId) {
      const firstPosting = next.find((account) => account.accepts_entries);
      setAccountId(firstPosting?.id ?? "");
    }
  }, [organizationId, accountId]);

  const loadReports = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);

    const [summaryResult, trialResult, incomeResult, positionResult] =
      await Promise.all([
        supabase.rpc("accounting_financial_summary", {
          p_organization_id: organizationId,
          p_date_from: dateFrom,
          p_date_to: dateTo,
        }),
        supabase.rpc("accounting_trial_balance", {
          p_organization_id: organizationId,
          p_date_from: dateFrom,
          p_date_to: dateTo,
        }),
        supabase.rpc("accounting_income_statement", {
          p_organization_id: organizationId,
          p_date_from: dateFrom,
          p_date_to: dateTo,
        }),
        supabase.rpc("accounting_financial_position", {
          p_organization_id: organizationId,
          p_as_of_date: dateTo,
        }),
      ]);

    const firstError =
      summaryResult.error ??
      trialResult.error ??
      incomeResult.error ??
      positionResult.error;

    if (firstError) setError(firstError.message);

    setSummary(
      summaryResult.error
        ? null
        : (((summaryResult.data ?? [])[0] as FinancialSummary | undefined) ??
            null),
    );
    setTrial(
      trialResult.error ? [] : ((trialResult.data ?? []) as TrialRow[]),
    );
    setIncome(
      incomeResult.error ? [] : ((incomeResult.data ?? []) as IncomeRow[]),
    );
    setPosition(
      positionResult.error
        ? []
        : ((positionResult.data ?? []) as PositionRow[]),
    );
    setLoading(false);
  }, [organizationId, dateFrom, dateTo]);

  const loadLedger = useCallback(async () => {
    if (!organizationId || !accountId) {
      setLedger([]);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc(
      "accounting_general_ledger",
      {
        p_organization_id: organizationId,
        p_account_id: accountId,
        p_date_from: dateFrom,
        p_date_to: dateTo,
      },
    );

    if (rpcError) {
      setLedger([]);
      setError(rpcError.message);
      setLoading(false);
      return;
    }

    setLedger((data ?? []) as LedgerRow[]);
    setLoading(false);
  }, [organizationId, accountId, dateFrom, dateTo]);

  useEffect(() => {
    void loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    void loadReports();
  }, [loadReports]);

  useEffect(() => {
    if (tab === "ledger") void loadLedger();
  }, [tab, loadLedger]);

  const selectedAccount = useMemo(
    () => accounts.find((account) => account.id === accountId) ?? null,
    [accounts, accountId],
  );

  function exportCurrentReport() {
    const suffix = `${dateFrom}-${dateTo}`;

    if (tab === "summary" && summary) {
      downloadCsv(
        `resumen-financiero-${suffix}.csv`,
        ["concepto", "importe"],
        [
          { concepto: "Activos", importe: summary.total_assets },
          { concepto: "Pasivos", importe: summary.total_liabilities },
          { concepto: "Patrimonio base", importe: summary.base_equity },
          { concepto: "Ingresos", importe: summary.total_income },
          { concepto: "Gastos", importe: summary.total_expenses },
          { concepto: "Resultado del periodo", importe: summary.period_result },
          { concepto: "Patrimonio con resultado", importe: summary.equity_with_result },
          { concepto: "Pasivo y patrimonio", importe: summary.liabilities_and_equity },
          { concepto: "Diferencia contable", importe: summary.accounting_difference },
        ],
      );
      return;
    }

    if (tab === "trial") {
      downloadCsv(
        `balance-comprobacion-${suffix}.csv`,
        ["cuenta", "nombre", "tipo", "apertura_debe", "apertura_haber", "periodo_debe", "periodo_haber", "cierre_debe", "cierre_haber"],
        trial.map((row) => ({
          cuenta: row.account_code,
          nombre: row.account_name,
          tipo: row.account_type,
          apertura_debe: row.opening_debit,
          apertura_haber: row.opening_credit,
          periodo_debe: row.period_debit,
          periodo_haber: row.period_credit,
          cierre_debe: row.closing_debit,
          cierre_haber: row.closing_credit,
        })),
      );
      return;
    }

    if (tab === "income") {
      downloadCsv(
        `estado-resultados-${suffix}.csv`,
        ["cuenta", "nombre", "tipo", "debe", "haber", "resultado"],
        income.map((row) => ({
          cuenta: row.account_code,
          nombre: row.account_name,
          tipo: row.account_type,
          debe: row.debit_total,
          haber: row.credit_total,
          resultado: row.report_balance,
        })),
      );
      return;
    }

    if (tab === "position") {
      downloadCsv(
        `estado-situacion-financiera-${dateTo}.csv`,
        ["cuenta", "nombre", "tipo", "debe", "haber", "saldo"],
        position.map((row) => ({
          cuenta: row.account_code,
          nombre: row.account_name,
          tipo: row.account_type,
          debe: row.debit_total,
          haber: row.credit_total,
          saldo: row.report_balance,
        })),
      );
      return;
    }

    if (tab === "ledger") {
      downloadCsv(
        `libro-mayor-${selectedAccount?.code ?? "cuenta"}-${suffix}.csv`,
        ["fecha", "asiento", "glosa", "detalle", "debe", "haber", "saldo"],
        ledger.map((row) => ({
          fecha: row.entry_date,
          asiento: row.entry_number,
          glosa: row.description,
          detalle: row.line_description ?? row.source_type ?? "",
          debe: row.debit,
          haber: row.credit,
          saldo: row.running_balance,
        })),
      );
    }
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Reportes</span>
          <h1>{selectedOrganization.name}</h1>
          <p>Estados financieros construidos desde asientos contabilizados.</p>
        </div>
      </header>

      <section className="reports-filters">
        <label>
          Desde
          <input
            type="date"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
          />
        </label>
        <label>
          Hasta
          <input
            type="date"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
          />
        </label>
        <button className="ghost-button" type="button" onClick={() => void loadReports()}>
          Actualizar
        </button>
        <button className="ghost-button" type="button" onClick={exportCurrentReport}>
          Exportar reporte
        </button>
      </section>

      {error ? <div className="inline-alert">{error}</div> : null}

      <section className="reports-tabs" aria-label="Tipos de reporte">
        {[
          ["summary", "Resumen"],
          ["trial", "Balance de comprobación"],
          ["income", "Resultados"],
          ["position", "Posición financiera"],
          ["ledger", "Mayor"],
        ].map(([value, label]) => (
          <button
            key={value}
            className={
              tab === value ? "compact-button active-filter" : "compact-button"
            }
            type="button"
            onClick={() => setTab(value as ReportTab)}
          >
            {label}
          </button>
        ))}
      </section>

      {loading ? (
        <section className="empty-state">
          <strong>Calculando reporte…</strong>
        </section>
      ) : null}

      {!loading && tab === "summary" && summary ? (
        <>
          <section className="reports-metric-grid">
            <div className="metric-card">
              <span>Activos</span>
              <strong>{money(summary.total_assets)}</strong>
            </div>
            <div className="metric-card">
              <span>Pasivos</span>
              <strong>{money(summary.total_liabilities)}</strong>
            </div>
            <div className="metric-card">
              <span>Patrimonio + resultado</span>
              <strong>{money(summary.equity_with_result)}</strong>
            </div>
            <div className="metric-card">
              <span>Ingresos</span>
              <strong>{money(summary.total_income)}</strong>
            </div>
            <div className="metric-card">
              <span>Gastos</span>
              <strong>{money(summary.total_expenses)}</strong>
            </div>
            <div className="metric-card">
              <span>Resultado</span>
              <strong>{money(summary.period_result)}</strong>
            </div>
          </section>

          <section className="panel-card reports-equation">
            <div>
              <span>Ecuación contable</span>
              <strong>
                {summary.equation_balanced ? "Cuadrada" : "Revisar diferencia"}
              </strong>
            </div>
            <div>
              <span>Diferencia</span>
              <strong>{money(summary.accounting_difference)}</strong>
            </div>
          </section>
        </>
      ) : null}

      {!loading && tab === "trial" ? (
        <section className="panel-card reports-table-wrap">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Cuenta</th>
                <th>Apertura D</th>
                <th>Apertura H</th>
                <th>Periodo D</th>
                <th>Periodo H</th>
                <th>Cierre D</th>
                <th>Cierre H</th>
              </tr>
            </thead>
            <tbody>
              {trial.map((row) => (
                <tr key={row.account_id}>
                  <td>
                    <strong>{row.account_code}</strong>
                    <span>{row.account_name}</span>
                  </td>
                  <td>{money(row.opening_debit)}</td>
                  <td>{money(row.opening_credit)}</td>
                  <td>{money(row.period_debit)}</td>
                  <td>{money(row.period_credit)}</td>
                  <td>{money(row.closing_debit)}</td>
                  <td>{money(row.closing_credit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {!loading && tab === "income" ? (
        <section className="panel-card reports-table-wrap">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Cuenta</th>
                <th>Tipo</th>
                <th>Debe</th>
                <th>Haber</th>
                <th>Resultado</th>
              </tr>
            </thead>
            <tbody>
              {income.map((row) => (
                <tr key={row.account_id}>
                  <td>
                    <strong>{row.account_code}</strong>
                    <span>{row.account_name}</span>
                  </td>
                  <td>{row.account_type}</td>
                  <td>{money(row.debit_total)}</td>
                  <td>{money(row.credit_total)}</td>
                  <td>{money(row.report_balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {!loading && tab === "position" ? (
        <section className="panel-card reports-table-wrap">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Cuenta</th>
                <th>Tipo</th>
                <th>Debe</th>
                <th>Haber</th>
                <th>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {position.map((row) => (
                <tr key={row.account_id}>
                  <td>
                    <strong>{row.account_code}</strong>
                    <span>{row.account_name}</span>
                  </td>
                  <td>{row.account_type}</td>
                  <td>{money(row.debit_total)}</td>
                  <td>{money(row.credit_total)}</td>
                  <td>{money(row.report_balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {!loading && tab === "ledger" ? (
        <>
          <section className="reports-ledger-account">
            <label>
              Cuenta
              <select
                value={accountId}
                onChange={(event) => setAccountId(event.target.value)}
              >
                <option value="">Seleccionar</option>
                {accounts
                  .filter((account) => account.accepts_entries)
                  .map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.code} · {account.name}
                    </option>
                  ))}
              </select>
            </label>
            <span>{selectedAccount?.account_type ?? ""}</span>
          </section>

          <section className="panel-card reports-table-wrap">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Asiento</th>
                  <th>Descripción</th>
                  <th>Debe</th>
                  <th>Haber</th>
                  <th>Saldo</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((row) => (
                  <tr key={row.entry_id + row.entry_number + row.line_description}>
                    <td>{row.entry_date}</td>
                    <td>{row.entry_number}</td>
                    <td>
                      <strong>{row.description}</strong>
                      <span>{row.line_description ?? row.source_type ?? ""}</span>
                    </td>
                    <td>{money(row.debit)}</td>
                    <td>{money(row.credit)}</td>
                    <td>{money(row.running_balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      ) : null}
    </div>
  );
}
